import {
  IStatementColumns,
  IStatementLine,
  StatementColumns,
  StatementLineType,
} from "shared";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";

import { DatePopover } from "../../../../common/Components/DatePopover";
import { formatAmount } from "../../../../common/lib/helpers";
import { classNames } from "../../../../common/lib/classNames";
import { SmallSpinner } from "../../../../common/Atoms/SmallSpinner";

import {
  AgePill,
  creditBadges,
  iconButton,
  isInvoiceLine,
  SelectBox,
  statementCellValue,
} from "./StatementLine";
import { columnLabel } from "./StatementHeading";
import { IStatementGroupHeading } from "./StatementLines";

interface StatementMobileLinesProps {
  lines: IStatementLine[];
  heading?: IStatementGroupHeading;
  columnSettings: IStatementColumns;
  organisationTimezone: string;
  downloadingInvoices?: Record<string, boolean>;
  onDownload: (line: IStatementLine) => void;
  selectedInvoiceIds: string[];
  onToggle: (line: IStatementLine) => void;
  onOpen: (line: IStatementLine) => void;
  showPayColumn?: boolean;
}

export function StatementMobileLines({
  lines,
  heading,
  columnSettings,
  organisationTimezone,
  downloadingInvoices,
  onDownload,
  selectedInvoiceIds,
  onToggle,
  onOpen,
  showPayColumn,
}: StatementMobileLinesProps) {
  if (!lines.length) {
    return null;
  }

  function dateColumn(line: IStatementLine) {
    if (line.type === StatementLineType.PaidInvoice) return StatementColumns.PaidDate;
    if (line.type === StatementLineType.Invoice) return StatementColumns.DueDate;

    return StatementColumns.Date;
  }

  function dateLine(line: IStatementLine) {
    const [prefix, date] = {
      [StatementColumns.PaidDate]: [`Paid`, line.paidDateString],
      [StatementColumns.DueDate]: [`Due`, line.invoiceDueDateString],
      [StatementColumns.Date]: [`Issued`, line.issueDateString],
    }[dateColumn(line)];

    if (!date) {
      return null;
    }

    return (
      <span>
        { `${prefix} ` }
        <DatePopover
          date={ date }
          organisationTimezone={ organisationTimezone }
          labelFormat={ `dd LLL yyyy` }
          time={ prefix === `Due` ? `end` : undefined }
          disablePopover
        />
      </span>
    );
  }

  return (
    <div className={ `flex flex-col` }>
      { heading && (
        <div className={ classNames(
          `flex justify-between gap-2`,
          heading.child
            ? `mt-2.5 rounded-lg bg-gray-50 px-3 py-2 text-[0.8125rem] font-semibold text-gray-900`
            : `px-0.5 pb-1.5 pt-4 text-xs font-semibold uppercase tracking-[0.04em] text-gray-500`,
        ) }>
          <span>{ heading.label }</span>
          <span className={ `text-[0.8125rem] font-medium tabular-nums normal-case tracking-normal text-gray-700` }>{ heading.total }</span>
        </div>
      ) }
      { lines.map((line, idx) => {
        const isInvoice = isInvoiceLine(line);
        const selected = selectedInvoiceIds.includes(line.invoiceId);
        const credit = creditBadges[line.type];
        const isPaid = line.type === StatementLineType.PaidInvoice;
        const downloading = !!downloadingInvoices?.[line.invoiceId];
        // Configured columns not already in the row summary
        const shown: string[] = [StatementColumns.Activity, StatementColumns.Reference, StatementColumns.OverdueAge, dateColumn(line)];

        if (!isPaid) {
          shown.push(StatementColumns.Balance);
        }

        const details = columnSettings.filter(column => !shown.includes(column.type));

        return (
          <div
            key={ `${line.invoiceId || line.externalId || line.invoiceNumber}-${idx}-mobile` }
            className={ classNames(
              `flex gap-3 border-b border-gray-100 px-0.5 py-3 tabular-nums`,
              isInvoice && `cursor-pointer`,
              selected && `bg-paidniceGreen-50`,
            ) }
            onClick={ () => isInvoice && onToggle(line) }
          >
            <span className={ `pt-0.5` }>
              <SelectBox
                checked={ selected }
                onChange={ () => onToggle(line) }
                disabled={ !isInvoice }
              />
            </span>
            <div className={ `flex min-w-0 flex-1 flex-col gap-1` }>
              <div className={ `flex justify-between gap-2.5` }>
                <span className={ `flex min-w-0 items-center gap-1.5` }>
                  { isInvoice ? (
                    <button
                      type={ `button` }
                      className={ `cursor-pointer truncate font-semibold text-paidniceGreen-600` }
                      onClick={ e => {
                        e.stopPropagation();
                        onOpen(line);
                      } }
                    >
                      { line.invoiceNumber }
                    </button>
                  ) : (
                    <span className={ classNames(`truncate font-semibold`, credit?.text) }>{ line.invoiceNumber }</span>
                  ) }
                  { credit && (
                    <span className={ classNames(`shrink-0 rounded border px-1 text-[0.6875rem] font-semibold`, credit.badge, credit.text) }>{ credit.title }</span>
                  ) }
                </span>
                <span className={ classNames(`whitespace-nowrap font-semibold`, credit ? credit.text : `text-gray-900`) }>
                  { formatAmount(isPaid ? line.totalInCents : line.outstandingInCents, line.currency) }
                </span>
              </div>
              { line.invoiceReference && (
                <span className={ `truncate text-[0.8125rem] text-gray-500` }>{ line.invoiceReference }</span>
              ) }
              <div className={ `flex items-center justify-between gap-2 text-[0.8125rem] text-gray-600` }>
                { dateLine(line) }
                <span className={ `flex items-center gap-2` }>
                  { line.type === StatementLineType.Invoice && <AgePill
                    days={ line.debtAgeInDays }
                    suffix={ `days overdue` }
                  /> }
                  { isInvoice && (
                    <button
                      type={ `button` }
                      title={ `Download Invoice PDF` }
                      className={ classNames(iconButton, downloading && `cursor-wait`) }
                      onClick={ e => {
                        e.stopPropagation();
                        onDownload(line);
                      } }
                      disabled={ downloading }
                    >
                      { downloading ? <SmallSpinner colored /> : <ArrowDownTrayIcon className={ `h-4 w-4` } /> }
                    </button>
                  ) }
                  { showPayColumn && line.paymentLink && (
                    <a
                      href={ line.paymentLink }
                      target={ `_blank` }
                      rel={ `noopener noreferrer` }
                      className={ `font-medium` }
                      onClick={ e => e.stopPropagation() }
                    >
                      { `Pay now` }
                    </a>
                  ) }
                </span>
              </div>
              { details.length > 0 && (
                <dl className={ `mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-xs` }>
                  { details.map(column => (
                    <div
                      key={ column.type }
                      className={ `min-w-0` }
                    >
                      <dt className={ `truncate text-gray-500` }>{ columnLabel(column) }</dt>
                      <dd className={ `truncate text-gray-800` }>{ statementCellValue(line, column.type, organisationTimezone) || `-` }</dd>
                    </div>
                  )) }
                </dl>
              ) }
            </div>
          </div>
        );
      }) }
    </div>
  );
}
